/**
 * Оплата заказа: `GET /payment-preview`, `POST /payment` (BE-211, FE-210).
 *
 * **Ни одного числа отсюда не приходит из кода фронта.** Цена, ставка
 * вознаграждения и фиксированная сумма живут в конфигурации backend и приходят
 * ответом: хардкод цены в интерфейсе и в текстах локалей запрещён
 * (MVP_TECH_RFC §25).
 *
 * Платёжных реквизитов в запросе нет и быть не может: провайдер — заглушка,
 * форма оплаты не имитируется (MVP_PRD §35.2).
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef } from 'react';

import { apiFetch } from '@shared/api/client';
import { queryKeys } from '@shared/api/queryKeys';
import { randomUuid } from '@shared/lib/uuid';

/** Условия вознаграждения по результату (MVP_PRD §35.5). */
export interface SuccessFeeTerms {
  enabled: boolean;
  /** Ставка в базисных пунктах: 2500 = 25 %. */
  rate_bps: number;
  /** Фиксированная сумма при нулевой скидке, в центах. */
  flat_cents: number;
}

export interface PaymentPreview {
  order_id: string;
  stage: string;
  paid: boolean;
  /** Можно ли оплатить сейчас: стадия подходит и рабочий набор не пуст. */
  payable: boolean;
  amount_cents: number;
  currency: string;
  /** 0 — платить не за что, экран оплаты не показывается (MVP_PRD §34.1). */
  shortlist_size: number;
  /** Коды состава услуги; тексты — через i18n. */
  includes: string[];
  success_fee: SuccessFeeTerms;
}

export interface Receipt {
  id: string;
  search_session_id: string | null;
  amount_cents: number;
  currency: string;
  /** `stub` — демонстрационный режим, и экран обязан сказать об этом прямо. */
  provider: string;
  status: string;
  receipt_number: string;
  paid_at: string | null;
  created_at: string;
}

/**
 * Три правовых согласия заказа (§ 305 Abs. 2 BGB, Art. 246a §1 Abs. 2 EGBGB,
 * § 356 Abs. 5 Nr. 2 BGB).
 *
 * Отдельные поля, а не одно «я со всем согласен»: за ними стоят три разные
 * нормы, и склеенная галочка не доказывает ни одной. Значений по умолчанию нет
 * — умолчание `true` было бы заранее отмеченным чекбоксом, только в коде.
 */
export interface OrderConsents {
  terms_accepted: boolean;
  withdrawal_notice_acknowledged: boolean;
  immediate_start_requested: boolean;
}

export interface PaymentResult {
  receipt: Receipt;
  stage: string;
  paid: boolean;
}

/**
 * Состояние платежа (Epic 48).
 *
 * Реквизитов карты здесь нет: `card_brand` и `card_last4` — то, что напечатано
 * на карте, и нужны они ровно для того, чтобы человек узнал свою.
 */
export interface PaymentStatus {
  payment_id: string;
  /**
   * `pending` | `requires_action` | `processing` | `requires_capture` |
   * `succeeded` | `failed` | `canceled` | `refunded` | `partially_refunded` |
   * `disputed`.
   */
  status: string;
  /** Пейволл открыт. `requires_capture` сюда **не входит**: деньги заморожены, а не получены. */
  paid: boolean;
  amount_cents: number;
  amount_captured_cents: number;
  amount_refunded_cents: number;
  currency: string;
  capture_method: string;
  /** Срок действия холда, ISO 8601. Назначает платёжная сеть, а не продукт. */
  capture_before: string | null;
  receipt_number: string;
  invoice_number: string | null;
  net_amount_cents: number;
  vat_amount_cents: number;
  vat_rate_bps: number;
  vat_scheme: string;
  vat_country: string;
  card_brand: string | null;
  card_last4: string | null;
  /** Код отказа эмитента; текст собирается по коду в i18n. */
  failure_code: string | null;
  decline_code: string | null;
}

export interface PaymentIntentSession {
  /**
   * Ключ формы оплаты. Действителен только для одного намерения и только в
   * этом браузере; секретного ключа платёжной системы в нём нет.
   */
  client_secret: string | null;
  publishable_key: string;
  payment: PaymentStatus;
  /** `false` — продолжается ранее начатая попытка, а не создана вторая форма. */
  created: boolean;
}

export type PaymentIntentRequest = OrderConsents & { save_card: boolean };

export function usePaymentPreview(searchId: string | null) {
  return useQuery({
    queryKey: queryKeys.paymentPreview(searchId ?? ''),
    queryFn: () =>
      apiFetch<PaymentPreview>(
        `/search-sessions/${encodeURIComponent(searchId ?? '')}/payment-preview`,
      ),
    enabled: searchId !== null && searchId !== '',
    // Тариф и состав услуги меняются релизом, а не в фоне: лишний запрос на
    // каждый рендер экрана оплаты не нужен.
    staleTime: 60_000,
    retry: false,
  });
}

/**
 * Оплатить заказ.
 *
 * **Ключ идемпотентности живёт до успеха, а не до рендера.** Двойной клик,
 * повтор после обрыва сети и «назад → вперёд» обязаны попасть в один и тот же
 * платёж; новый ключ на каждую попытку означал бы второе списание, если бы
 * провайдер был настоящим.
 */
export function usePayOrder(searchId: string) {
  const queryClient = useQueryClient();
  // Ключ создаётся **один раз на экран**, а не на рендер: пересоздание на
  // каждом рендере превратило бы повтор в новый платёж — ровно то, от чего
  // ключ идемпотентности и защищает.
  const key = useRef<string>('');
  if (key.current === '') key.current = randomUuid();

  return useMutation({
    mutationFn: (consents: OrderConsents) =>
      apiFetch<PaymentResult>(`/search-sessions/${encodeURIComponent(searchId)}/payment`, {
        method: 'POST',
        idempotencyKey: key.current,
        // Согласия уходят телом запроса: сервис без них отвечает `422`
        // (§ 312j Abs. 4 BGB — договор без подтверждения платёжной обязанности
        // не заключается). Чекбоксы на экране — их отражение, а не защита.
        body: consents,
      }),
    onSuccess: async () => {
      // Оплата меняет стадию заказа и открывает данные рабочего набора:
      // кеш заказа, прогона и результатов после неё недостоверен.
      await queryClient.invalidateQueries({ queryKey: queryKeys.searchSession(searchId) });
      await queryClient.invalidateQueries({ queryKey: ['search-run'] });
      await queryClient.invalidateQueries({ queryKey: queryKeys.paymentPreview(searchId) });
    },
  });
}

/**
 * Открыть платёжную форму: создать намерение оплаты и получить `client_secret`.
 *
 * **Заказ после этого не оплачен.** Деньги ещё не двигались: подтверждение
 * происходит в форме платёжной системы, а результат приходит событием — на
 * этом и держится правило «не полагаться на редирект» (§48.4).
 *
 * Ключ идемпотентности живёт **до успеха, а не до рендера**: пересоздание на
 * каждом рендере означало бы вторую форму оплаты того же заказа, то есть два
 * возможных списания.
 */
export function useCreatePaymentIntent(searchId: string) {
  const key = useRef<string>('');
  if (key.current === '') key.current = randomUuid();

  return useMutation({
    mutationFn: (request: PaymentIntentRequest) =>
      apiFetch<PaymentIntentSession>(
        `/search-sessions/${encodeURIComponent(searchId)}/payment-intent`,
        { method: 'POST', idempotencyKey: key.current, body: request },
      ),
  });
}

/**
 * Подтянуть состояние платежа сразу после подтверждения формы.
 *
 * **Не замена событию.** Тот же результат придёт конвертом платёжной системы,
 * и пейволл откроется, даже если закрыть вкладку в момент подтверждения.
 * Смысл вызова один — не заставлять человека ждать доставки события, чтобы
 * увидеть результат собственного нажатия.
 */
export function useSyncPayment(searchId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch<PaymentStatus>(`/search-sessions/${encodeURIComponent(searchId)}/payment/sync`, {
        method: 'POST',
      }),
    onSuccess: async (payment) => {
      // Ответ кладётся в кеш статуса **до** любых инвалидаций. Иначе экран
      // успевает увидеть «заказ оплачен» из перезапрошенного тарифа раньше,
      // чем узнаёт подробности платежа, — и уходит дальше, не показав
      // человеку ни суммы, ни номера счёта.
      queryClient.setQueryData(queryKeys.paymentStatus(searchId), payment);
      if (!payment.paid) return;
      await queryClient.invalidateQueries({ queryKey: queryKeys.searchSession(searchId) });
      await queryClient.invalidateQueries({ queryKey: ['search-run'] });
      await queryClient.invalidateQueries({ queryKey: queryKeys.paymentPreview(searchId) });
    },
  });
}

/**
 * Статусы, из которых платёж ещё может стать успешным.
 *
 * `requires_capture` сюда **не входит**: замороженные деньги — законченное
 * состояние, решение по нему принимает не экран. Ждать его изменения значило
 * бы опрашивать сервер неделю.
 */
const PENDING_PAYMENT_STATUSES = new Set(['pending', 'requires_action', 'processing']);

/**
 * Ждёт ли платёж новости, ради которой стоит опрашивать сервер.
 *
 * Отсутствие данных — **тоже ожидание**: запрос мог ещё не вернуться, а
 * платёж уже подтверждён в форме. Считать «нет данных» законченным состоянием
 * значило бы прекратить опрос ровно в тот момент, когда он нужен.
 */
export function isPaymentPending(payment: PaymentStatus | null | undefined): boolean {
  if (payment === null || payment === undefined) return true;
  return PENDING_PAYMENT_STATUSES.has(payment.status);
}

/**
 * Состояние платежа по заказу. `null` — попыток оплаты ещё не было.
 *
 * Опрос включается только после подтверждения формы и прекращается сам, как
 * только платёж вышел из промежуточного состояния. Держать его всё время
 * означало бы запрос раз в две секунды на экране, который уже всё показал.
 */
export function usePaymentStatus(searchId: string | null, { watch }: { watch: boolean }) {
  return useQuery({
    queryKey: queryKeys.paymentStatus(searchId ?? ''),
    queryFn: () =>
      apiFetch<PaymentStatus | null>(
        `/search-sessions/${encodeURIComponent(searchId ?? '')}/payment-status`,
      ),
    enabled: searchId !== null && searchId !== '',
    refetchInterval: (query) => (watch && isPaymentPending(query.state.data) ? 2_000 : false),
    retry: false,
  });
}
